import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useLocation } from 'react-router';
import type { MeResponse, RelatedSectionKey, ThreadsQuery } from '@platform/shared';
import { RELATED_SECTIONS } from '@platform/shared';
import { useIsPhone } from '../../hooks/useMediaQuery';
import { deriveRouteContext, entityOf, linkOf, railTypeOf, type ContextFocus, type PageContext } from '../../shell/pageContext';
import { useRelated } from '../related/useRelated';

/**
 * AI Assist's state (planning-app.md §3.6 *AI Assist* → *The panel*, `v3.15a`, PA64), held by
 * the shell so it survives navigation: whether the panel is open, the drawer's width (remembered
 * per person in localStorage, 360–720), the view inside it (home, the Threads tabs, a thread), and
 * the `PageContext` — the route entity, view and filters from the URL, the entity rail's ids and
 * counts from the same `useRelated` query the page's rail holds (one cache entry, no second
 * request), and the row a page says has focus (`useAssistFocus`). The card follows the page: the
 * context is recomputed on every location change while the panel stays where it was.
 *
 * On a phone the panel is a full-height sheet closed by *Back*: opening pushes a history entry
 * carrying `assist: true` beside the router's own state, `popstate` reads it back, and the close
 * button goes back rather than popping the entry by hand, so the two never disagree.
 */
export const ASSIST_WIDTH_DEFAULT = 420;
export const ASSIST_WIDTH_MIN = 360;
export const ASSIST_WIDTH_MAX = 720;
/** `localStorage` key of the remembered width, per person. */
export const widthStorageKey = (personId: string) => `planning.assist.width.${personId}`;
/**
 * From this width the drawer takes its own column beside the page (outside the entity rail, IA §4); below it, it lies
 * over the page's edge. 1440, not the rules' 1280 for side panels (v3.18 lane 14, the critique's §3.17): a 420px
 * panel beside content needs ~1,000px of it (rules §1), and at 1280 the 220px nav and the panel leave 640px — a
 * 12-column dashboard below its floor, and the reader's three columns (TOC, paper, rail) folding onto each other.
 */
export const ASSIST_DOCKED_QUERY = '(min-width: 1440px)';

export type ThreadsTab = 'here' | 'recent' | 'mine' | 'search';
export type AssistView = { kind: 'home' } | { kind: 'threads'; tab: ThreadsTab; q?: string } | { kind: 'thread'; id: string };

/** The panel's own state: what the shell, the button and the drawer frame read. Changes on open / close, a resize, a view change, the phone cut. */
export interface AssistShellState {
  me: MeResponse;
  open: boolean;
  phone: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  width: number;
  setWidth: (width: number) => void;
  view: AssistView;
  setView: (view: AssistView) => void;
  /** The element focus returns to when the panel closes (the top bar's button). */
  openerRef: RefObject<HTMLButtonElement | null>;
}
/** The page's context: what the panel's content reads. Changes on every navigation and when the rail's counts arrive — the shell does not re-render for it. */
export interface AssistPageState {
  context: PageContext;
  /** The `GET /api/threads` filter of the page's own threads (*Here*): by link, by citation on a document; null when the page has no entity. */
  hereQuery: ThreadsQuery | null;
  setFocus: (focus: ContextFocus | null) => void;
}
export type AssistState = AssistShellState & AssistPageState;

const AssistShellContext = createContext<AssistShellState | null>(null);
const AssistPageContext = createContext<AssistPageState | null>(null);

export const clampWidth = (w: number): number => Math.min(ASSIST_WIDTH_MAX, Math.max(ASSIST_WIDTH_MIN, Math.round(w)));

function readWidth(personId: string): number {
  try {
    const raw = window.localStorage.getItem(widthStorageKey(personId));
    const n = raw === null ? NaN : Number(raw);
    return Number.isFinite(n) ? clampWidth(n) : ASSIST_WIDTH_DEFAULT;
  } catch {
    return ASSIST_WIDTH_DEFAULT;
  }
}

type AssistHistoryState = { assist?: boolean } | null;
const historyAssist = (): boolean => Boolean((window.history.state as AssistHistoryState)?.assist);

export function AssistProvider({ me, children }: { me: MeResponse; children: ReactNode }) {
  const location = useLocation();
  const phone = useIsPhone();
  const personId = me.person.id;
  const [open, setOpenState] = useState(false);
  const [width, setWidthState] = useState(() => readWidth(personId));
  const [view, setView] = useState<AssistView>({ kind: 'home' });
  // The focused row belongs to the page it was set on: keyed by the location, so a page's own effect (which runs before this provider's) is not undone by a reset here.
  const routeKey = `${location.pathname}${location.search}`;
  const routeKeyRef = useRef(routeKey);
  routeKeyRef.current = routeKey;
  const [focusState, setFocusState] = useState<{ key: string; focus: ContextFocus | null }>({ key: '', focus: null });
  const setFocus = useCallback((f: ContextFocus | null) => setFocusState({ key: routeKeyRef.current, focus: f }), []);
  const focus = focusState.key === routeKey ? focusState.focus : null;
  const openerRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => setWidthState(readWidth(personId)), [personId]);
  const setWidth = useCallback(
    (w: number) => {
      const next = clampWidth(w);
      setWidthState(next);
      try {
        window.localStorage.setItem(widthStorageKey(personId), String(next));
      } catch {
        /* private mode: the width lasts the session */
      }
    },
    [personId],
  );

  const setOpen = useCallback(
    (next: boolean) => {
      if (phone) {
        // The sheet is a history entry on a phone: *Back* closes it, and so does the close button, through the same door.
        if (next && !historyAssist()) window.history.pushState({ ...(window.history.state as object | null), assist: true }, '');
        else if (!next && historyAssist()) {
          window.history.back();
          return;
        }
      }
      setOpenState(next);
    },
    [phone],
  );
  const toggle = useCallback(() => setOpen(!open), [open, setOpen]);

  // Back on a phone closes the sheet; forward to the entry reopens it.
  useEffect(() => {
    if (!phone) return;
    const onPop = () => setOpenState(historyAssist());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [phone]);
  // A navigation under the sheet (a link inside it — to another path or to the same path with other filters, `/threads?mine=1`)
  // shows the page: the sheet closes; the drawer on a desktop stays. The router gives every navigation a new `key`;
  // the sheet's own history entry is pushed outside the router and never changes it, and Back from the sheet
  // returns to the entry the router already holds, so neither reads as a navigation here.
  const keyRef = useRef(location.key);
  useEffect(() => {
    if (keyRef.current === location.key) return;
    keyRef.current = location.key;
    if (phone && open && !historyAssist()) setOpenState(false);
  }, [location.key, phone, open]);
  // Leaving the phone cut while open (a rotation, a resize) drops the history entry's meaning; the panel stays open as the drawer.
  useEffect(() => {
    if (!phone && historyAssist()) window.history.replaceState({ ...(window.history.state as object | null), assist: false }, '');
  }, [phone]);

  // ⌘/Ctrl+J toggles the panel; Escape closes the drawer (the page stays interactive, so a key, not a trap — A11Y: a modal sheet stops its own Escape first).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && (e.key === 'j' || e.key === 'J')) {
        e.preventDefault();
        setOpen(!open);
        return;
      }
      // A draft in a text area is worth more than the key: Escape there is left alone.
      if (e.key === 'Escape' && open && !e.defaultPrevented && !(e.target instanceof HTMLTextAreaElement)) {
        setOpen(false);
        openerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  // The page context: the URL, then the rail's counts from the same query the page's rail holds — subscribed to only
  // while the panel is open, so a closed panel costs the page no request and no render.
  const route = useMemo(() => deriveRouteContext(location), [location]);
  // An embedded page's entity (a story in the list's side panel, a decision in a track's tab) is the page's scope — the
  // card, *Here*, the thread's link and the rail follow it; a row focus (a section, an element) stays a focus.
  const entity = useMemo(() => entityOf(route.entity, focus), [route.entity, focus]);
  const rowFocus = entity === route.entity ? (focus ?? route.focus) : null;
  const railRef = railTypeOf(entity);
  const related = useRelated(railRef?.type ?? 'track', open && railRef ? railRef.id : '');
  const rail = useMemo(() => {
    const r = related.data;
    if (!railRef || !r) return null;
    const out: NonNullable<PageContext['rail']> = {};
    for (const k of RELATED_SECTIONS as readonly RelatedSectionKey[]) out[k] = { count: r[k].count, ids: r[k].ids };
    return out;
  }, [railRef, related.data]);
  const context = useMemo<PageContext>(() => ({ route: route.route, entity, view: route.view, filters: route.filters, rail, focus: rowFocus }), [route, entity, rail, rowFocus]);
  const hereQuery = useMemo<ThreadsQuery | null>(() => {
    const link = linkOf(entity);
    if (link) return { entityType: link.entityType, entityId: link.entityId };
    if (entity?.kind === 'doc') return { cites: entity.id };
    return null;
  }, [entity]);

  const shell = useMemo<AssistShellState>(() => ({ me, open, phone, setOpen, toggle, width, setWidth, view, setView, openerRef }), [me, open, phone, setOpen, toggle, width, setWidth, view]);
  const pageState = useMemo<AssistPageState>(() => ({ context, hereQuery, setFocus }), [context, hereQuery, setFocus]);
  return (
    <AssistShellContext.Provider value={shell}>
      <AssistPageContext.Provider value={pageState}>{children}</AssistPageContext.Provider>
    </AssistShellContext.Provider>
  );
}

/** The panel's state alone — for the shell, the button and the drawer frame, which must not re-render with the page's context. */
export function useAssistShell(): AssistShellState {
  const v = useContext(AssistShellContext);
  if (!v) throw new Error('useAssistShell outside AssistProvider');
  return v;
}

/** The panel's state, or null outside the shell — for a page's own *Discuss this* button, which opens the panel on what is in view (v3.15d). */
export function useAssistShellOptional(): AssistShellState | null {
  return useContext(AssistShellContext);
}

/** Both halves — for the panel's content. */
export function useAssist(): AssistState {
  const s = useAssistShell();
  const p = useContext(AssistPageContext);
  if (!p) throw new Error('useAssist outside AssistProvider');
  return useMemo(() => ({ ...s, ...p }), [s, p]);
}

/** The page half, or null outside the shell (the sign-in page, the authorize page): the panel is not there. */
export function useAssistOptional(): AssistPageState | null {
  return useContext(AssistPageContext);
}

/**
 * A page publishes the row that has focus (a decision in its list, a section in view) for the context card and the
 * first message's `context.focus`; it clears when the page navigates away. No-op outside the shell.
 */
export function useAssistFocus(focus: ContextFocus | null): void {
  const assist = useAssistOptional();
  const setFocus = assist?.setFocus;
  const kind = focus?.kind ?? null;
  const id = focus?.id ?? null;
  // Re-published on every location change: the provider keys a focus by the location, and a page that stays mounted
  // across one (a filter changed beside `?open=`) would otherwise lose it.
  const { pathname, search } = useLocation();
  useEffect(() => {
    if (!setFocus) return;
    setFocus(kind && id ? { kind, id } : null);
  }, [setFocus, kind, id, pathname, search]);
}
